/**
 * Translation setup (i18next + react-i18next).
 *
 * - All user-facing text goes through t() / useTranslation(); keys are
 *   type-checked against the English files (see i18next.d.ts).
 * - One JSON file per namespace in locales/<lang>/; namespaces roughly follow
 *   feature folders. English is the source of truth; other languages must
 *   have the same keys (enforced by i18n.test.ts).
 * - Interpolate values with {{name}}; never concatenate translated fragments
 *   (word order differs between languages). Plurals use i18next's _one/_other
 *   suffixes. Dates and numbers go through Intl with the current language.
 * - Language: browser preference among SUPPORTED_LANGUAGES, falling back to
 *   English. (A per-user setting can be added once a second language ships.)
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import account from "./locales/en/account.json";
import admin from "./locales/en/admin.json";
import adminCompany from "./locales/en/adminCompany.json";
import adminContentSafety from "./locales/en/adminContentSafety.json";
import adminFeedback from "./locales/en/adminFeedback.json";
import adminGovernance from "./locales/en/adminGovernance.json";
import adminIntegrations from "./locales/en/adminIntegrations.json";
import adminLicense from "./locales/en/adminLicense.json";
import adminModels from "./locales/en/adminModels.json";
import adminPlatform from "./locales/en/adminPlatform.json";
import adminUsage from "./locales/en/adminUsage.json";
import agents from "./locales/en/agents.json";
import brands from "./locales/en/brands.json";
import build from "./locales/en/build.json";
import buildChat from "./locales/en/buildChat.json";
import chat from "./locales/en/chat.json";
import common from "./locales/en/common.json";
import connectors from "./locales/en/connectors.json";
import dataSources from "./locales/en/dataSources.json";
import devAuth from "./locales/en/devAuth.json";
import errors from "./locales/en/errors.json";
import feedback from "./locales/en/feedback.json";
import help from "./locales/en/help.json";
import home from "./locales/en/home.json";
import knowledge from "./locales/en/knowledge.json";
import microApps from "./locales/en/microApps.json";
import notifications from "./locales/en/notifications.json";
import settings from "./locales/en/settings.json";
import shell from "./locales/en/shell.json";
import skills from "./locales/en/skills.json";
import spaces from "./locales/en/spaces.json";
import teams from "./locales/en/teams.json";
import workflows from "./locales/en/workflows.json";

export const resources = {
  en: {
    common,
    shell,
    spaces,
    home,
    settings,
    account,
    admin,
    adminModels,
    adminUsage,
    adminGovernance,
    adminContentSafety,
    adminCompany,
    adminLicense,
    adminFeedback,
    adminPlatform,
    teams,
    adminIntegrations,
    errors,
    devAuth,
    chat,
    build,
    buildChat,
    agents,
    skills,
    connectors,
    dataSources,
    brands,
    workflows,
    microApps,
    knowledge,
    help,
    notifications,
    feedback,
  },
} as const;

export const SUPPORTED_LANGUAGES = Object.keys(resources) as Array<keyof typeof resources>;
export const DEFAULT_NAMESPACE = "common";

export function detectLanguage(preferred: readonly string[] = globalThis.navigator?.languages ?? []): string {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split("-")[0] ?? "";
    if ((SUPPORTED_LANGUAGES as string[]).includes(base)) return base;
  }
  return "en";
}

export function initI18n(lng = detectLanguage()) {
  if (i18n.isInitialized) return i18n;
  void i18n.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng: "en",
    ns: Object.keys(resources.en),
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: { escapeValue: false }, // React escapes output
    returnNull: false,
    initAsync: false, // resources are bundled: render translated on first paint
  });
  i18n.on("languageChanged", (l) => document.documentElement.setAttribute("lang", l));
  document.documentElement.setAttribute("lang", i18n.language);
  return i18n;
}

export { i18n };
